import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useMultiTenant } from '@/hooks/useMultiTenant';
import { addDays, addMonths, endOfMonth, getDate } from 'date-fns';
import type { PaymentCondition } from '@/types/paymentCondition';

export const filterConditionsByValue = (
  conditions: PaymentCondition[],
  saleTotal: number
): PaymentCondition[] => {
  return conditions.filter(condition => {
    const minValue = condition.config.min_sale_value || 0;
    const maxValue = condition.config.max_sale_value || Infinity;
    return saleTotal >= minValue && saleTotal <= maxValue;
  });
};

export const calculateDueDates = (
  condition: PaymentCondition,
  saleDate: Date,
  closingTermDays?: number,
  cutoffDay?: number
): Date[] => {
  // L-FAT.1 D-FAT.5/D-FAT.6: fechamento mensal — um vencimento no último dia do
  // mês da venda (data LOCAL, sem serialização UTC) + prazo do cliente; ignora config.days.
  if (condition.config.month_end === true) {
    // L-FAT.1 D-FAT.19-rev: corte do CLIENTE (customers.cutoff_day) — venda após o dia de corte fecha no mês seguinte
    const base = (typeof cutoffDay === 'number' && cutoffDay > 0 && getDate(saleDate) > cutoffDay) ? addMonths(saleDate, 1) : saleDate;
    return [addDays(endOfMonth(base), closingTermDays ?? 0)];
  }

  switch (condition.condition_type) {
    case 'cash':
      return [saleDate];
    
    case 'by_due_date':
      const days = Array.isArray(condition.config.days) 
        ? condition.config.days 
        : typeof condition.config.days === 'number' && condition.config.days > 0
          ? [condition.config.days]
          : [30];
      return days.map(d => addDays(saleDate, d));
    
    case 'by_installments':
      const numInst = condition.config.installments || 1;
      const baseDays = Array.isArray(condition.config.days) ? condition.config.days : [];
      if (baseDays.length === numInst) {
        return baseDays.map(d => addDays(saleDate, d));
      }
      // Fallback: parcelas mensais (30 em 30 dias)
      return Array.from({ length: numInst }, (_, i) => addDays(saleDate, 30 * (i + 1)));
    
    default:
      return [saleDate];
  }
};

const EMPTY_CONDITIONS: PaymentCondition[] = [];

export const usePaymentConditions = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { companyId } = useMultiTenant();

  const query = useQuery({
    queryKey: ['payment-conditions', companyId],
    queryFn: async () => {
      if (!companyId) throw new Error('Company ID não disponível');
      
      const { data, error } = await supabase
        .from('payment_conditions')
        .select('*')
        .eq('company_id', companyId)
        .eq('active', true)
        .order('name');

      if (error) throw error;
      return (data || []) as PaymentCondition[];
    },
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: async (condition: Omit<PaymentCondition, 'id' | 'created_at' | 'updated_at' | 'company_id'>) => {
      if (!companyId) throw new Error('Company ID não disponível');
      
      const { data, error } = await supabase
        .from('payment_conditions')
        .insert([{ ...condition, company_id: companyId }])
        .select()
        .single();

      if (error) throw error;
      return data as PaymentCondition;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-conditions'] });
      toast({ title: 'Condição de pagamento criada com sucesso' });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao criar condição de pagamento',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<PaymentCondition> & { id: string }) => {
      const { data, error } = await supabase
        .from('payment_conditions')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-conditions'] });
      toast({ title: 'Condição de pagamento atualizada com sucesso' });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao atualizar condição de pagamento',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // ── 145D-1c.3: update de config em lote (formas aceitas no manager) ──────
  // Sem RPC de lote no banco; volume ~22 linhas — updates individuais em
  // sequência, invalidação única ao final. O caller entrega o config já
  // mesclado (merge preservante de days/installments/min_sale_value).
  const bulkConfigMutation = useMutation({
    mutationFn: async (updates: { id: string; config: PaymentCondition['config'] }[]) => {
      for (const { id, config } of updates) {
        const { error } = await supabase
          .from('payment_conditions')
          .update({ config })
          .eq('id', id);

        if (error) throw error;
      }
      return updates.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ['payment-conditions'] });
      toast({ title: `Formas aceitas atualizadas em ${count} condição(ões)` });
    },
    onError: (error: any) => {
      // Falha no meio do lote: parte pode ter sido aplicada — invalida para
      // o grid refletir o estado real do banco.
      queryClient.invalidateQueries({ queryKey: ['payment-conditions'] });
      toast({
        title: 'Erro ao atualizar formas aceitas',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('payment_conditions')
        .update({ active: false })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-conditions'] });
      toast({ title: 'Condição de pagamento excluída com sucesso' });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao excluir condição de pagamento',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    conditions: query.data ?? EMPTY_CONDITIONS,
    isLoading: query.isLoading,
    createCondition: createMutation.mutate,
    updateCondition: updateMutation.mutate,
    deleteCondition: deleteMutation.mutate,
    bulkSetConditionConfigs: bulkConfigMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isBulkUpdating: bulkConfigMutation.isPending,
    calculateDueDates,
    filterConditionsByValue,
  };
};
